import { useState, useEffect, useCallback } from 'react';
import { dataTrackEvents } from 'const';
import useVideoContext from './useVideoContext';

export default () => {
  const [, setPublications] = useState([]);
  const [, setDataTracks] = useState([]);
  const [isCallOnHold, setIsCallOnHold] = useState(false);

  const { room, localTracks } = useVideoContext();
  const localDataTrack = localTracks.find((track) => track.kind === 'data');

  const sendDataTrackMsg = useCallback(
    (event, data = {}) => {
      if (localDataTrack && localDataTrack.send) {
        localDataTrack.send(JSON.stringify({ event, data }));
      }
    },
    [localDataTrack],
  );

  const notifyOnHoldStatus = useCallback(
    (enabled) => {
      sendDataTrackMsg(enabled ? dataTrackEvents.providerOnHold : dataTrackEvents.providerResume, { identity: room.localParticipant.identity });
    },
    [sendDataTrackMsg],
  );

  const handleVideoMessages = useCallback((event, data) => {
    switch (event) {
      case dataTrackEvents.requestOnHoldStatus: {
        notifyOnHoldStatus(false);
        break;
      }
      case dataTrackEvents.providerOnHold: {
        setIsCallOnHold(true);
        break;
      }
      case dataTrackEvents.providerResume: {
        setIsCallOnHold(false);
        break;
      }
      default:
        console.log('unhandled Video Messages:', event, data); // eslint-disable-line no-console
    }
  }, []);

  const processDataTrackMessage = (msg) => {
    const json = JSON.parse(msg);
    const isVideoMessage = [
      dataTrackEvents.providerOnHold,
      dataTrackEvents.providerResume,
      dataTrackEvents.requestOnHoldStatus,
      dataTrackEvents.startedSharingScreen,
      dataTrackEvents.stoppedSharingScreen,
    ].includes(json.event);

    if (isVideoMessage) handleVideoMessages(json.event, json.data);
  };

  const addTrack = (track) => {
    track.on('message', processDataTrackMessage);
    sendDataTrackMsg(dataTrackEvents.requestOnHoldStatus);
    setDataTracks((tracks) => [...tracks, track]);
  };

  const removeTrack = (track) => {
    track.off('message', processDataTrackMessage);
    setDataTracks((tracks) => tracks.filter((x) => x.sid !== track.sid));
  };

  useEffect(() => {
    if (room) {
      const updatePublications = () => {
        setPublications((existingPublications) => {
          existingPublications.forEach((publication) => {
            if (publication) {
              publication.off('subscribed', addTrack);
              publication.off('unsubscribed', removeTrack);
            }
          });
          const newPublications = Array.from(room.participants.values())
            .map((participant) => Array.from(participant.tracks.values()).filter((track) => track.kind === 'data')[0])
            .filter((x) => x);

          newPublications.forEach((publication) => {
            if (publication) {
              publication.on('subscribed', addTrack);
              publication.on('unsubscribed', removeTrack);
            }
          });
          return newPublications;
        });
      };
      updatePublications();

      room.on('trackPublished', updatePublications);
      room.on('trackUnpublished', updatePublications);
      room.on('participantDisconnected', updatePublications);

      room.localParticipant.on('trackPublished', updatePublications);
      room.localParticipant.on('trackUnpublished', updatePublications);

      room.localParticipant.on('trackSubscribed', updatePublications);
      room.localParticipant.on('trackStopped', updatePublications);
      room.localParticipant.on('trackRemoved', updatePublications);

      return () => {
        room.off('trackPublished', updatePublications);
        room.off('trackUnpublished', updatePublications);
        room.off('participantDisconnected', updatePublications);

        room.localParticipant.off('trackPublished', updatePublications);
        room.localParticipant.off('trackUnpublished', updatePublications);
        room.localParticipant.off('trackSubscribed', updatePublications);
        room.localParticipant.off('trackStopped', updatePublications);
        room.localParticipant.off('trackRemoved', updatePublications);

        setPublications((existingPublications) => {
          existingPublications.forEach((publication) => {
            publication.off('subscribed', addTrack);
            publication.off('unsubscribed', removeTrack);
          });
          return [];
        });

        setDataTracks((existingTracks) => {
          existingTracks.forEach((track) => {
            track.off('message', processDataTrackMessage);
          });
          return [];
        });
      };
    }
  }, [room]);

  return {
    isCallOnHold,
  };
};
